import { Box } from "@mui/material";
import {
	UTILITY_CONSUMPTION_WINDOW,
	type UtilityConsumptionMeterGap,
	type UtilityConsumptionSummary,
	type UtilityConsumptionWindow,
} from "../../models/UtilityConsumption";
import {
	formatCurrency,
	formatVolume,
	formatVolumeWithUnit,
	VOLUME_UNIT,
} from "../../utils/format";
import { getMeterGapNote } from "../../utils/meterGaps";
import { ConsumptionMetric } from "../ConsumptionMetric";
import { ConsumptionCard, metricRowSx } from "./ConsumptionCard";

const formatMillions = (value: number | null) => {
	if (value === null) return null;
	if (Math.abs(value) < 1_000_000) return formatVolume(value);
	return `${(value / 1_000_000).toLocaleString("en-US", {
		maximumFractionDigits: 1,
		minimumFractionDigits: 1,
	})}M`;
};

export const TotalsSummaryCard = ({
	summary,
	meterGaps,
	showCost,
	plantTimeZone,
}: {
	summary: UtilityConsumptionSummary;
	meterGaps: UtilityConsumptionMeterGap[];
	showCost: boolean;
	plantTimeZone: string;
}) => {
	const gapOf = (windowCode: UtilityConsumptionWindow) =>
		getMeterGapNote(meterGaps, windowCode, plantTimeZone);
	const monthToDateGap = gapOf(UTILITY_CONSUMPTION_WINDOW.MONTH_TO_DATE);
	const lastMonthGap = gapOf(UTILITY_CONSUMPTION_WINDOW.LAST_MONTH);
	const yearToDateGap = gapOf(UTILITY_CONSUMPTION_WINDOW.YEAR_TO_DATE);
	const averageMonthGap = gapOf(UTILITY_CONSUMPTION_WINDOW.COMPLETE_MONTHS);
	const billedGallons = formatVolume(summary.LastMonthBilledGallons);
	const billedAmount = showCost
		? formatCurrency(summary.LastMonthBilledAmount)
		: null;

	const billed =
		billedGallons === null
			? null
			: `Billed ${billedGallons} ${VOLUME_UNIT}${
					billedAmount === null ? "" : ` · ${billedAmount}`
				}`;
	return (
		<ConsumptionCard
			area="totals"
			label="Monthly and annual consumption totals"
			upper={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label={`Total to date · ${summary.CurrentMonthLabel}`}
						value={formatMillions(summary.MonthToDateGallons)}
						unit={VOLUME_UNIT}
						secondary={monthToDateGap?.secondary}
						tooltip={
							monthToDateGap?.tooltip ??
							formatVolumeWithUnit(summary.MonthToDateGallons)
						}
					/>
					<ConsumptionMetric
						label={`Last month total · ${summary.PreviousMonthLabel}`}
						value={formatMillions(summary.LastMonthTotalGallons)}
						unit={VOLUME_UNIT}
						secondary={
							lastMonthGap !== null ? (
								lastMonthGap.secondary
							) : billed === null ? (
								<Box component="span" sx={{ opacity: 0.5 }}>
									Billed not available
								</Box>
							) : (
								billed
							)
						}
						tooltip={
							lastMonthGap?.tooltip ??
							formatVolumeWithUnit(summary.LastMonthTotalGallons)
						}
					/>
				</Box>
			}
			lower={
				<Box sx={metricRowSx}>
					<ConsumptionMetric
						label="Total · YTD"
						value={formatMillions(summary.YearToDateGallons)}
						unit={VOLUME_UNIT}
						secondary={yearToDateGap?.secondary}
						tooltip={
							yearToDateGap?.tooltip ??
							formatVolumeWithUnit(summary.YearToDateGallons)
						}
					/>
					<ConsumptionMetric
						label="Avg per Month"
						value={formatMillions(summary.AverageMonthGallons)}
						unit={VOLUME_UNIT}
						secondary={averageMonthGap?.secondary}
						tooltip={
							averageMonthGap?.tooltip ??
							(summary.AverageMonthGallons === null
								? "No whole month of this year has been measured yet."
								: `${formatVolumeWithUnit(summary.AverageMonthGallons)} · the mean of the ${summary.AverageMonthCount} whole months of this year the data covers.`)
						}
					/>
				</Box>
			}
		/>
	);
};
